import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../../api/client.js';
import { useGenerateSpec, useSpecDecision, useSpecification } from '../../api/projects.js';
import { Alert, Button, Card, Spinner } from '../../components/ui.js';
import { SpecView } from '../../components/SpecView.js';
import { SpecComments } from '../../components/Discussion.js';
import { useProjectContext } from './ProjectShell.js';

/** The reassuring spinner shown while the draft is being written. */
function GeneratingCard() {
  return (
    <Card>
      <div className="flex items-center gap-3">
        <Spinner />
        <div>
          <h2 className="font-title text-2xl tracking-wide">Generating your specification…</h2>
          <p className="text-sm opacity-70">
            We’re turning your questionnaire answers into a draft specification. Our team then
            reviews it and shares it back here for your feedback and approval — nothing is built
            until you say so. This usually takes under a minute; you can leave this page and come
            back.
          </p>
        </div>
      </div>
    </Card>
  );
}

export function SpecificationPage() {
  const { project } = useProjectContext();
  const spec = useSpecification(project.id);
  const generate = useGenerateSpec(project.id);
  const decide = useSpecDecision(project.id);
  const qc = useQueryClient();
  const [note, setNote] = useState('');
  const [showNote, setShowNote] = useState(false);

  // The project polls itself while generating (see useProject); when it finishes
  // we pull the freshly-created spec in.
  const generating = project.status === 'generating_spec';
  const wasGenerating = useRef(generating);
  useEffect(() => {
    if (wasGenerating.current && !generating) {
      void qc.invalidateQueries({ queryKey: ['specification', project.id] });
    }
    wasGenerating.current = generating;
  }, [generating, project.id, qc]);

  // There's nothing for the founder to do here but ask us to write the draft, so
  // we do it for them: as soon as they land with no spec yet, kick off generation
  // automatically. It fires once. If required answers are still missing the server
  // refuses cheaply (before any model call) and we fall back to a "finish the
  // questionnaire" prompt. A genuine failure is shown with a manual retry.
  const autoStarted = useRef(false);
  const canAutoGenerate =
    !generating && !spec.isPending && !spec.data && !generate.isPending && !generate.isError;
  useEffect(() => {
    if (canAutoGenerate && !autoStarted.current) {
      autoStarted.current = true;
      generate.mutate();
    }
  }, [canAutoGenerate, generate]);

  if (generating || generate.isPending) return <GeneratingCard />;

  if (spec.isPending) return <Card>{<Spinner />}</Card>;

  // No specification yet. Generation is automatic (see the effect above), so the
  // only reasons we're still here are: it's about to start, required answers are
  // missing, or it failed — each gets its own explanation.
  if (!spec.data) {
    const err = generate.error instanceof ApiError ? generate.error : null;
    const missing =
      err?.code === 'missing_answers'
        ? ((err.details as { missing?: string[] } | undefined)?.missing ?? [])
        : null;

    // The one case the founder must act on: finish the questionnaire first.
    if (missing) {
      return (
        <Card>
          <h2 className="mb-2 font-title text-2xl tracking-wide">Draft specification</h2>
          <p className="mb-3 opacity-70">
            Your specification is written automatically from your questionnaire — but a few required
            answers are still missing, so we can’t start yet.
          </p>
          <div className="mb-4">
            <Alert>Please answer these questions first: {missing.join(', ')}.</Alert>
          </div>
          <Link to="../questionnaire" className="text-primary underline">
            Back to the questionnaire →
          </Link>
        </Card>
      );
    }

    if (err?.code === 'ai_not_configured') {
      return (
        <Card>
          <h2 className="mb-2 font-title text-2xl tracking-wide">Draft specification</h2>
          <Alert>
            Specification generation isn’t set up yet. Please contact your Aldemis developer.
          </Alert>
        </Card>
      );
    }

    if (generate.isError) {
      return (
        <Card>
          <h2 className="mb-2 font-title text-2xl tracking-wide">Draft specification</h2>
          <div className="mb-4">
            <Alert>We couldn’t generate your specification just now. Please try again.</Alert>
          </div>
          <Button disabled={generate.isPending} onClick={() => generate.mutate()}>
            {generate.isPending ? 'Generating…' : 'Try again'}
          </Button>
        </Card>
      );
    }

    // About to auto-start — show the same reassuring generating state.
    return <GeneratingCard />;
  }

  const status = project.status;
  const awaitingFounder = status === 'spec_shared';
  const inReview = status === 'spec_review' || status === 'generating_spec';

  return (
    <div className="flex flex-col gap-4">
      {inReview ? (
        <Alert kind="info">
          Your draft specification is being reviewed by our team. We’ll share the finalised document
          for your approval shortly.
        </Alert>
      ) : status === 'spec_approved' ? (
        <Alert kind="info">You approved this specification — your project is ready to build.</Alert>
      ) : status === 'building' ? (
        <Alert kind="info">Your project is being built from this specification.</Alert>
      ) : null}

      <Card>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-title text-2xl tracking-wide">Specification</h2>
          <span className="text-sm opacity-60">
            v{spec.data.version} · {spec.data.status.replace('_', ' ')}
          </span>
        </div>
        <SpecView content={spec.data.content} downloadName={project.name} />

        {awaitingFounder ? (
          <div className="mt-5 flex flex-col gap-2 border-t border-secondary/10 pt-4">
            <p className="text-sm font-medium">Do you approve this specification?</p>
            {decide.isError ? (
              <Alert>Could not record your decision. Please try again.</Alert>
            ) : null}
            {showNote ? (
              <textarea
                className="min-h-16 rounded-md border border-secondary/20 bg-white px-3 py-2 outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                placeholder="What should change? Our team will see this."
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            ) : null}
            <div className="flex gap-2">
              <Button
                disabled={decide.isPending}
                onClick={() => decide.mutate({ decision: 'approve' })}
              >
                Approve
              </Button>
              {showNote ? (
                <Button
                  variant="secondary"
                  disabled={decide.isPending || !note.trim()}
                  onClick={() => decide.mutate({ decision: 'request_changes', note: note.trim() })}
                >
                  Send request
                </Button>
              ) : (
                <Button variant="secondary" onClick={() => setShowNote(true)}>
                  Request changes…
                </Button>
              )}
            </div>
          </div>
        ) : null}
      </Card>

      <Card>
        <h2 className="mb-3 font-title text-xl tracking-wide">Discussion</h2>
        <SpecComments projectId={project.id} canMarkInternal={false} />
      </Card>
    </div>
  );
}
